import type { GenericBlok } from '@/components/customFields/types';
import type { Tokens } from '@/components/pdf/pdfUtils';
import type { PillCardProps } from '@/components/pillCard/PillCard';
import type { UserState } from '@/stores/UserStore';
import type { EntityVtwCategoryStoryblok, PageVtwBenefitStoryblok } from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';

import type { ReplacementsType } from './replacements';
import { EventCategory, EventSource, type TrackingInformation } from './tracking';
import { resolveStories } from './utils';

export type PageVtwBenefitProps = {
  story: ISbStoryData<PageVtwBenefitStoryblok>;
  breadcrumbs: ISbStoryData<GenericBlok>[];
  latestArticles?: PillCardProps[];
};

export function getVtwReplacementTexts(
  user: UserState | undefined,
  validFromDmy?: string,
  validToDmy?: string,
  link?: string
): ReplacementsType {
  const replacements: ReplacementsType = [];

  if (validFromDmy && validToDmy) {
    replacements.push({
      search: '%%zeitraum%%',
      replace: `${validFromDmy} bis ${validToDmy}`,
    });
  }

  if (user?.name?.firstname && user?.name.lastname) {
    replacements.push({
      search: '%%name%%',
      replace: `${user.name.firstname} ${user.name.lastname}`,
    });
  }

  if (user?.name?.firstname) {
    replacements.push({
      search: '%%vorname%%',
      replace: user.name.firstname,
    });
  }

  if (user?.name?.lastname) {
    replacements.push({
      search: '%%nachname%%',
      replace: user.name.lastname,
    });
  }

  if (validFromDmy) {
    replacements.push({
      search: '%%beginn%%',
      replace: validFromDmy,
    });
  }

  if (validToDmy) {
    replacements.push({
      search: '%%ende%%',
      replace: validToDmy,
    });
  }

  if (link) {
    replacements.push({
      search: '%%link%%',
      replace: link,
    });
  }

  return replacements;
}

export const getTokens = (): Tokens => {
  const rootStyles = getComputedStyle(document.documentElement);
  return {
    colors: {
      colorGreen100: rootStyles.getPropertyValue('--color-green-100').trim(),
      colorGreen600: rootStyles.getPropertyValue('--color-green-600').trim(),
      colorGrey400: rootStyles.getPropertyValue('--color-grey-400').trim(),
      colorGrey600: rootStyles.getPropertyValue('--color-grey-600').trim(),
      colorGrey800: rootStyles.getPropertyValue('--color-grey-800').trim(),
    },
  };
};

export function getTrackingItemFromVTWPage(
  vtwPage: PageVtwBenefitStoryblok | undefined,
  listName: EventSource | undefined,
  index: number
): TrackingInformation {
  const categories = resolveStories<EntityVtwCategoryStoryblok>(vtwPage?.category);
  return {
    index,
    itemName: vtwPage?.headline.toLowerCase() ?? '',
    itemId: vtwPage?._uid ?? '',
    itemCategory: EventCategory.Vorteilswelt,
    itemCategory2: categories && (categories[0]?.content.name ?? ''),
    itemListName: listName,
  };
}
